import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  CylinderGeometry,
  Group,
  InstancedMesh,
  Matrix4,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import {
  TRAFFIC_SIGNAL_RENDER_CONFIG,
  type TrafficSignalPhaseGroup,
} from "../../config/trafficSignals.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import type {
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import { streetFeatureRoadEdgeWidthM } from "../../world/streets.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import {
  trafficSignalSourcesForTile,
  type TileTrafficSignalSource,
} from "../../world/trafficSignals.ts";

export interface TrafficSignalBatch {
  readonly object: Group;
  readonly signalCount: number;
  readonly skippedCount: number;
}

interface RoadProjection {
  readonly road: Readonly<RuntimeLinearFeature>;
  readonly x: number;
  readonly z: number;
  readonly tangentX: number;
  readonly tangentZ: number;
  readonly distanceM: number;
  readonly signedSideDistanceM: number;
  readonly widthM: number;
}

export interface TrafficSignalPlacement {
  readonly source: Readonly<TileTrafficSignalSource>;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly phaseGroup: TrafficSignalPhaseGroup;
  readonly usedMappedRoadsidePosition: boolean;
  readonly placementKind: "mapped-curb" | "derived-curb" | "junction-corner";
}

const SIGNAL_HEAD_X_M = 0.28;
const SIGNAL_GROUND_LIFT_M = 0.015;
const CARRIAGEWAY_CLEARANCE_EPSILON_M = 0.05;

function translated(
  geometry: BufferGeometry,
  x: number,
  y: number,
  z: number,
): BufferGeometry {
  geometry.translate(x, y, z);
  return geometry;
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
  if (merged === null) {
    throw new Error(`Could not merge ${name}`);
  }
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function createHousingGeometry(): BufferGeometry {
  const config = TRAFFIC_SIGNAL_RENDER_CONFIG;
  const housingCenterY = config.poleHeightM - config.housingHeightM / 2;
  return mergeOwned([
    translated(
      new CylinderGeometry(
        config.poleRadiusM * 1.9,
        config.poleRadiusM * 2.35,
        0.16,
        8,
      ),
      0,
      0.08,
      0,
    ),
    translated(
      new CylinderGeometry(
        config.poleRadiusM,
        config.poleRadiusM * 1.18,
        config.poleHeightM,
        8,
      ),
      0,
      config.poleHeightM / 2,
      0,
    ),
    translated(
      new BoxGeometry(SIGNAL_HEAD_X_M * 2, 0.075, 0.075),
      SIGNAL_HEAD_X_M / 2,
      config.poleHeightM - 0.08,
      0,
    ),
    translated(
      new BoxGeometry(
        config.housingWidthM,
        config.housingHeightM,
        config.housingDepthM,
      ),
      SIGNAL_HEAD_X_M,
      housingCenterY,
      0,
    ),
    translated(
      new BoxGeometry(
        config.housingWidthM + 0.06,
        0.055,
        config.housingDepthM + 0.08,
      ),
      SIGNAL_HEAD_X_M,
      config.poleHeightM + 0.025,
      0,
    ),
  ], "vilnius-traffic-signal:housing");
}

function createLensGeometry(verticalOffsetM: number): BufferGeometry {
  const config = TRAFFIC_SIGNAL_RENDER_CONFIG;
  const geometry = new CircleGeometry(config.lensRadiusM, 12);
  geometry.translate(
    SIGNAL_HEAD_X_M,
    config.poleHeightM - config.housingHeightM / 2 + verticalOffsetM,
    config.housingDepthM / 2 + 0.006,
  );
  geometry.name = "vilnius-traffic-signal:lens";
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function roadProjections(
  source: Readonly<TileTrafficSignalSource>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
): readonly RoadProjection[] {
  const projections: RoadProjection[] = [];
  for (const road of roads) {
    let nearestForRoad: RoadProjection | null = null;
    for (let index = 1; index < road.points.length; index += 1) {
      const from = road.points[index - 1];
      const to = road.points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
      if (lengthSquared <= 0.01) {
        continue;
      }
      const alpha = Math.max(0, Math.min(1,
        ((source.position.x - from.x) * deltaX +
          (source.position.z - from.z) * deltaZ) / lengthSquared,
      ));
      const x = from.x + deltaX * alpha;
      const z = from.z + deltaZ * alpha;
      const distanceM = Math.hypot(
        source.position.x - x,
        source.position.z - z,
      );
      if (
        nearestForRoad === null ||
        distanceM < nearestForRoad.distanceM
      ) {
        const length = Math.sqrt(lengthSquared);
        const tangentX = deltaX / length;
        const tangentZ = deltaZ / length;
        nearestForRoad = {
          road,
          x,
          z,
          tangentX,
          tangentZ,
          distanceM,
          signedSideDistanceM:
            (source.position.x - x) * tangentZ -
            (source.position.z - z) * tangentX,
          widthM: streetFeatureRoadEdgeWidthM(road),
        };
      }
    }
    if (nearestForRoad !== null) {
      projections.push(nearestForRoad);
    }
  }
  return projections.sort((left, right) => left.distanceM - right.distanceM);
}

function distinctJunctionAxes(
  projections: readonly RoadProjection[],
): readonly RoadProjection[] {
  const nearest = projections[0];
  if (nearest === undefined) {
    return [];
  }
  const axes = [nearest];
  if (
    nearest.distanceM >
      TRAFFIC_SIGNAL_RENDER_CONFIG.junctionAxisToleranceM
  ) {
    return axes;
  }
  for (const candidate of projections.slice(1)) {
    if (
      candidate.distanceM >
        TRAFFIC_SIGNAL_RENDER_CONFIG.junctionAxisToleranceM
    ) {
      break;
    }
    const duplicatesExistingAxis = axes.some((axis) =>
      Math.abs(
        axis.tangentX * candidate.tangentX +
          axis.tangentZ * candidate.tangentZ,
      ) > TRAFFIC_SIGNAL_RENDER_CONFIG.distinctAxisMaximumDot
    );
    if (!duplicatesExistingAxis) {
      axes.push(candidate);
    }
  }
  return axes;
}

function terrainHeightForPlacement(
  terrain: Readonly<TerrainTileData>,
  source: Readonly<TileTrafficSignalSource>,
  x: number,
  z: number,
): number | null {
  return sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    { x, z },
  ) ?? sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    source.position,
  );
}

function placementAtRoadside(
  source: Readonly<TileTrafficSignalSource>,
  projection: Readonly<RoadProjection>,
  terrain: Readonly<TerrainTileData>,
  side: -1 | 1,
  alongOffsetM: number,
  placementKind: TrafficSignalPlacement["placementKind"],
): TrafficSignalPlacement | null {
  const normalX = projection.tangentZ;
  const normalZ = -projection.tangentX;
  const roadsideOffsetM = projection.widthM / 2 +
    TRAFFIC_SIGNAL_RENDER_CONFIG.curbClearanceM;
  const x = projection.x +
    projection.tangentX * alongOffsetM +
    normalX * roadsideOffsetM * side;
  const z = projection.z +
    projection.tangentZ * alongOffsetM +
    normalZ * roadsideOffsetM * side;
  const terrainY = terrainHeightForPlacement(terrain, source, x, z);
  if (terrainY === null) {
    return null;
  }

  // Lithuania drives on the right. A pole on +normal serves traffic moving
  // with the line tangent; its lenses therefore face against that tangent.
  const frontX = -projection.tangentX * side;
  const frontZ = -projection.tangentZ * side;
  return {
    source,
    x,
    y: terrainY + SIGNAL_GROUND_LIFT_M,
    z,
    yaw: Math.atan2(frontX, frontZ),
    phaseGroup: Math.abs(projection.tangentX) >=
        Math.abs(projection.tangentZ)
      ? "axis-a"
      : "axis-b",
    usedMappedRoadsidePosition: false,
    placementKind,
  };
}

function placementsFor(
  source: Readonly<TileTrafficSignalSource>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
): readonly TrafficSignalPlacement[] {
  const projections = roadProjections(source, roads);
  const projection = projections[0];
  if (
    projection === null ||
    projection === undefined ||
    projection.distanceM >
      TRAFFIC_SIGNAL_RENDER_CONFIG.maximumSourceToRoadDistanceM
  ) {
    return [];
  }

  const axes = distinctJunctionAxes(projections);
  if (axes.length > 1) {
    return axes.flatMap((axis) => {
      const crossingHalfWidthM = Math.max(
        ...axes
          .filter((candidate) => candidate !== axis)
          .map((candidate) => candidate.widthM / 2),
      );
      const setbackM = crossingHalfWidthM +
        TRAFFIC_SIGNAL_RENDER_CONFIG.junctionSetbackM;
      return ([-1, 1] as const).flatMap((side) => {
        const placement = placementAtRoadside(
          source,
          axis,
          terrain,
          side,
          -side * setbackM,
          "junction-corner",
        );
        return placement === null ? [] : [placement];
      });
    });
  }

  const requiredRoadsideDistanceM = projection.widthM / 2 +
    TRAFFIC_SIGNAL_RENDER_CONFIG.curbClearanceM;
  if (projection.distanceM >= requiredRoadsideDistanceM) {
    const side = projection.signedSideDistanceM < 0 ? -1 : 1;
    const frontX = -projection.tangentX * side;
    const frontZ = -projection.tangentZ * side;
    const terrainY = terrainHeightForPlacement(
      terrain,
      source,
      source.position.x,
      source.position.z,
    );
    return terrainY === null ? [] : [{
      source,
      x: source.position.x,
      y: terrainY + SIGNAL_GROUND_LIFT_M,
      z: source.position.z,
      yaw: Math.atan2(frontX, frontZ),
      phaseGroup: Math.abs(projection.tangentX) >=
          Math.abs(projection.tangentZ)
        ? "axis-a"
        : "axis-b",
      usedMappedRoadsidePosition: true,
      placementKind: "mapped-curb",
    }];
  }

  const sides: readonly (-1 | 1)[] =
    Math.abs(projection.signedSideDistanceM) <=
        TRAFFIC_SIGNAL_RENDER_CONFIG.centrelineSideDeadZoneM ||
      source.direction === "both"
      ? [-1, 1]
      : [projection.signedSideDistanceM < 0 ? -1 : 1];
  return sides.flatMap((side) => {
    const placement = placementAtRoadside(
      source,
      projection,
      terrain,
      side,
      0,
      "derived-curb",
    );
    return placement === null ? [] : [placement];
  });
}

export function createTrafficSignalBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  groundingTerrain: Readonly<TerrainTileData> = tile.terrain,
): TrafficSignalBatch | null {
  const sources = trafficSignalSourcesForTile(tile);
  if (sources.length === 0) {
    return null;
  }
  const placements = trafficSignalPlacementsForTile(tile, groundingTerrain);
  if (placements.length === 0) {
    return null;
  }

  const object = new Group();
  object.name = `runtime-traffic-signals:${tile.id}`;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "openstreetmap-highway-traffic-signals",
    sourceLicense: "ODbL-1.0",
    sourceSignalCount: sources.length,
    renderedSignalCount: new Set(
      placements.map((placement) => placement.source.id),
    ).size,
    renderedPoleCount: placements.length,
    skippedSignalCount: sources.length - new Set(
      placements.map((placement) => placement.source.id),
    ).size,
    mappedRoadsidePositionCount: placements.filter(
      (placement) => placement.usedMappedRoadsidePosition,
    ).length,
    junctionCornerCount: placements.filter(
      (placement) => placement.placementKind === "junction-corner",
    ).length,
    placement: "source-position-real-width-curb-and-junction-corners",
    representation: "vilnius-dark-vertical-led-low-poly",
  };

  const housing = new InstancedMesh(
    createHousingGeometry(),
    materials.trafficSignalHousing,
    placements.length,
  );
  housing.name = `${object.name}:housings`;
  housing.castShadow = false;
  housing.receiveShadow = false;
  const matrix = new Matrix4();
  for (const [index, placement] of placements.entries()) {
    matrix.makeRotationY(placement.yaw);
    matrix.setPosition(placement.x, placement.y, placement.z);
    housing.setMatrixAt(index, matrix);
  }
  housing.instanceMatrix.needsUpdate = true;
  object.add(housing);

  const lensOffsets = {
    red: 0.3,
    amber: 0,
    green: -0.3,
  } as const;
  for (const phaseGroup of ["axis-a", "axis-b"] as const) {
    const phasePlacements = placements.filter(
      (placement) => placement.phaseGroup === phaseGroup,
    );
    if (phasePlacements.length === 0) {
      continue;
    }
    for (const colour of ["red", "amber", "green"] as const) {
      const lenses = new InstancedMesh(
        createLensGeometry(lensOffsets[colour]),
        materials.trafficSignalLenses[phaseGroup][colour],
        phasePlacements.length,
      );
      lenses.name = `${object.name}:${phaseGroup}:${colour}`;
      lenses.castShadow = false;
      lenses.receiveShadow = false;
      for (const [index, placement] of phasePlacements.entries()) {
        matrix.makeRotationY(placement.yaw);
        matrix.setPosition(placement.x, placement.y, placement.z);
        lenses.setMatrixAt(index, matrix);
      }
      lenses.instanceMatrix.needsUpdate = true;
      object.add(lenses);
    }
  }
  return {
    object,
    signalCount: placements.length,
    skippedCount: sources.length - new Set(
      placements.map((placement) => placement.source.id),
    ).size,
  };
}

export function trafficSignalPlacementsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  groundingTerrain: Readonly<TerrainTileData> = tile.terrain,
): readonly TrafficSignalPlacement[] {
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  return trafficSignalSourcesForTile(tile)
    .flatMap((source) => placementsFor(source, roads, groundingTerrain))
    .filter((placement) =>
      !tile.buildings.some((building) =>
        pointInPolygon(placement, building.footprint)
      ) &&
      !roads.some((road) =>
        distanceToPolyline(placement, road.points) <
          streetFeatureRoadEdgeWidthM(road) / 2 +
            CARRIAGEWAY_CLEARANCE_EPSILON_M
      )
    );
}

function distanceToPolyline(
  point: Readonly<{ x: number; z: number }>,
  line: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1];
    const to = line[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const amount = lengthSquared === 0
      ? 0
      : Math.max(0, Math.min(1,
          ((point.x - from.x) * deltaX + (point.z - from.z) * deltaZ) /
            lengthSquared,
        ));
    minimum = Math.min(minimum, Math.hypot(
      point.x - (from.x + deltaX * amount),
      point.z - (from.z + deltaZ * amount),
    ));
  }
  return minimum;
}
